import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { sessionStorage } from '@shared/helpers/storage'
import env from '@shared/env.js'
import useUserStore from '@shared/stores/useUserStore.js'

// Bump when the persisted shape changes, so a session persisted by an earlier build is dropped.
const STORE_VERSION = 3

// Answer keys per submission scope, as the API names them. pain-location is
// one answer on screen and is also serialized as worst_pain_location.
const COPD_KEYS = [
  'gender',
  'pain_level',
  'fitness_activities',
  'fear-of-movement',
  'red_flag_approved',
]
const MSK_KEYS = [
  'gender',
  'pain-location',
  'pain_level',
  'chronification_level',
  'fitness_activities',
  'fear-of-movement',
  'red_flag_approved',
]
// MSK EU's full post-activation set also asks the goal; the run with a stored
// goal uses postSignup instead.
const MSK_EU_KEYS = [...MSK_KEYS, 'goal', 'goal_freetext']
const fullKeys = () => {
  if (env.isCopd) return COPD_KEYS
  return env.isMskEu ? MSK_EU_KEYS : MSK_KEYS
}
const SCOPE_KEYS = {
  // env is a build-time constant, so the full scope is fixed per variant.
  full: fullKeys(),
  preSignup: [
    'pain-location',
    'pain_level',
    'chronification_level',
    'previous_treatment',
    'goal',
    'goal_freetext',
  ],
  // The completing submission after a partial. Gender was always asked on
  // these screens but never sent; it belongs here.
  postSignup: [
    'gender',
    'pain_level',
    'fitness_activities',
    'fear-of-movement',
    'red_flag_approved',
  ],
}

export const useSelfTestStore = defineStore(
  'selfTest',
  () => {
    // State

    // Confirmed answers with the copy the patient saw, keyed by API answer key.
    // Single: { value, text, questionText, language }. List (fitness_activities):
    // { answers: [{ value, text }], questionText, language }.
    const answers = ref({})
    const hasCompletedPreSignupAssessment = ref(false)
    const needsRestart = ref(false)
    // MSK EU asks the NPRS again after activation. Once the pre-signup assessment
    // is done, the answer held here is the earlier one: the pain screen starts
    // blank and the completing submission leaves it out until it is answered again.
    // The answer itself stays: the pre-signup summary still reads it.
    const painLevelAwaitsSecondAnswer = ref(false)
    const version = ref(STORE_VERSION)

    const scoped = (scope) => {
      const keys = SCOPE_KEYS[scope]
      if (!keys) throw new Error(`Unknown self-test submission scope: ${scope}`)
      return Object.entries(answers.value).filter(
        ([key]) =>
          keys.includes(key) &&
          !(
            scope !== 'preSignup' &&
            key === 'pain_level' &&
            painLevelAwaitsSecondAnswer.value
          ),
      )
    }

    // Serializers

    // The legacy endpoint and the tracking event take bare values.
    const bareAnswers = (scope) =>
      Object.fromEntries(
        scoped(scope).flatMap(([key, snapshot]) => {
          if (key === 'pain-location') {
            return [
              ['pain-location', [snapshot.value]],
              ['worst_pain_location', snapshot.value],
            ]
          }
          if (snapshot.answers) {
            return [[key, snapshot.answers.map((option) => option.value)]]
          }
          return [[key, snapshot.value]]
        }),
      )

    // POST /v2/user/self_test takes every answer with its copy. The language is
    // the first answer's; the screens hide the language picker, so a scope's
    // answers are usually confirmed in one language. Switching on sign-up and
    // re-confirming one answer is the exception the contract can't express.
    const submission = (scope) => {
      const entries = scoped(scope)
      // Each answer names its own language: a patient can switch on sign-up and
      // confirm one screen again, and the contract files each answer's copy under
      // the language it was read in.
      const answerObjects = entries.flatMap(([key, snapshot]) => {
        const copy = {
          question_text: snapshot.questionText,
          language: snapshot.language,
        }
        if (key === 'pain-location') {
          const option = { value: snapshot.value, text: snapshot.text }
          return [
            ['pain-location', { ...copy, answers: [option] }],
            ['worst_pain_location', { ...copy, ...option }],
          ]
        }
        if (snapshot.answers) {
          return [[key, { ...copy, answers: snapshot.answers }]]
        }
        return [[key, { ...copy, value: snapshot.value, text: snapshot.text }]]
      })
      // Every scope a call site submits has at least one confirmed answer, so
      // the language is always present; the contract requires it.
      return {
        language: entries[0]?.[1].language,
        answers: Object.fromEntries(answerObjects),
      }
    }

    // Readers used outside the self test; they keep the shapes the old refs had.
    const valueOf = (key) => answers.value[key]?.value
    const gender = computed(() => valueOf('gender') ?? null)
    const worstPainLocation = computed(() => valueOf('pain-location') ?? '')
    const painIntensity = computed(() => valueOf('pain_level') ?? -1)
    const goal = computed(() => valueOf('goal') ?? '')
    const chronificationLevel = computed(
      () => valueOf('chronification_level') ?? null,
    )
    // The option config keys previous treatment by boolean.
    const previousTreatment = computed(() => {
      const value = valueOf('previous_treatment')
      return value == null ? null : value === 'previous-treatment-true'
    })
    const redFlagApproved = computed(
      () => answers.value.red_flag_approved != null,
    )

    // Actions

    // Stamps the language now: the sign-up page shows the language switcher, so
    // copy read in one language must not be tagged with a later one.
    function answer(key, snapshot) {
      if (key === 'pain_level') painLevelAwaitsSecondAnswer.value = false
      answers.value = {
        ...answers.value,
        [key]: { ...snapshot, language: useUserStore().lang },
      }
    }

    function clearAnswer(key) {
      const { [key]: _removed, ...rest } = answers.value
      answers.value = rest
    }

    function markPreSignupComplete() {
      hasCompletedPreSignupAssessment.value = true
      painLevelAwaitsSecondAnswer.value =
        env.isMskEu && answers.value.pain_level != null
    }

    return {
      // State
      answers,
      hasCompletedPreSignupAssessment,
      needsRestart,
      painLevelAwaitsSecondAnswer,
      version,
      // Serializers
      bareAnswers,
      submission,
      // Readers
      gender,
      worstPainLocation,
      painIntensity,
      goal,
      chronificationLevel,
      previousTreatment,
      redFlagApproved,
      // Actions
      answer,
      clearAnswer,
      markPreSignupComplete,
    }
  },
  {
    persist: {
      storage: sessionStorage,
      serializer: {
        serialize: JSON.stringify,
        // Discard incompatible answers and make the assessment restart before rendering a step.
        deserialize: (raw) => {
          const persisted = JSON.parse(raw)
          return persisted.version === STORE_VERSION
            ? persisted
            : { needsRestart: true }
        },
      },
    },
  },
)

export default useSelfTestStore
